import type { MouseEvent, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { AnnotatedRichText } from '@/components/blocks/editor/proposal-diff/annotations';
import { ControlledRichTextEditor } from '@/components/editor/controlled-rich-text-editor';
import { placeholderForSlot } from '@/components/editor/inline-rich-text-placeholders';
import {
  INLINE_RICH_TEXT_EDIT_CURSOR_CLASS,
  INLINE_RICH_TEXT_SURFACE_CLASS,
  inlineRichTextPreviewCursorClass,
} from '@/components/editor/inline-rich-text-surface';
import { getMainSections } from '@/lib/page-utils';
import { hasRichTextContent } from '@/lib/rich-text';
import { normalizeRichTextContent } from '@/lib/rich-text/normalize-rich-text';
import { useBlockFieldDiff } from '@/lib/section-proposal-diff/section-review-context';
import { isRichTextFieldDiff } from '@/lib/section-proposal-diff/types';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';

interface BlockInlineTextEditorProps {
  sectionId: string;
  block: PageBlock;
  isEditing: boolean;
  /** When true, preview uses default cursor (block not draggable). */
  dragDisabled?: boolean;
  onRequestEdit?: () => void;
  onFocusChange?: (isFocused: boolean) => void;
  onExitEditing?: () => void;
  toolbarAnchorRef?: RefObject<HTMLElement | null>;
  className?: string;
}

// Mirrors Page::MAX_TITLE_LENGTH on the backend.
const PAGE_TITLE_MAX_LENGTH = 255;
const TITLE_SYNCED_SECTION_TYPES = new Set(['case_result', 'blog']);
const isTitleSyncedSectionType = (sectionType: string | undefined): boolean =>
  !!sectionType && TITLE_SYNCED_SECTION_TYPES.has(sectionType);

export function BlockInlineTextEditor({
  sectionId,
  block,
  isEditing,
  dragDisabled = false,
  onRequestEdit,
  onFocusChange,
  onExitEditing,
  toolbarAnchorRef,
  className,
}: BlockInlineTextEditorProps) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const sectionType = usePageEditorStore((state) => {
    const section = getMainSections(state.page).find((s) => s.id === sectionId);
    return section?.sectionType;
  });
  const resolvedContent = normalizeRichTextContent(block.props?.content);
  const editorContent = hasRichTextContent(resolvedContent) ? resolvedContent : '';
  const slot = block.props?.slot;
  const isTitleSlot = slot === 'title';
  const isTitleSyncedSlot = isTitleSlot && isTitleSyncedSectionType(sectionType);
  const headingsOnly = isTitleSyncedSlot;
  const maxPlainTextLength = isTitleSyncedSlot ? PAGE_TITLE_MAX_LENGTH : undefined;
  const placeholder = placeholderForSlot(slot);
  const fieldDiff = useBlockFieldDiff(block.id);
  const latestPropsRef = useRef(block.props || {});

  useEffect(() => {
    latestPropsRef.current = block.props || {};
  }, [block.props]);

  const persistHtml = useCallback(
    (html: string) => {
      updateBlock(sectionId, block.id, {
        props: { ...latestPropsRef.current, content: html },
      });
    },
    [block.id, sectionId, updateBlock],
  );

  const handleFocusChange = useCallback(
    (nextFocused: boolean) => {
      onFocusChange?.(nextFocused);
    },
    [onFocusChange],
  );

  const handleEnterEdit = useCallback(
    (event?: MouseEvent) => {
      if (!isEditing) {
        event?.stopPropagation();
        onRequestEdit?.();
      }
    },
    [isEditing, onRequestEdit],
  );

  const previewCursorClass = inlineRichTextPreviewCursorClass(dragDisabled);

  return (
    <div
      data-block-text-editor-root
      onDoubleClick={!isEditing ? handleEnterEdit : undefined}
      className={cn(
        'w-full',
        isEditing ? INLINE_RICH_TEXT_EDIT_CURSOR_CLASS : previewCursorClass,
        className,
      )}
    >
      {!isEditing && <span data-block-text-preview-root />}
      {!isEditing && fieldDiff?.hasChanges && isRichTextFieldDiff(fieldDiff) ? (
        <div className={cn(INLINE_RICH_TEXT_SURFACE_CLASS, previewCursorClass)}>
          <AnnotatedRichText fieldDiff={fieldDiff} />
        </div>
      ) : (
        <ControlledRichTextEditor
          value={editorContent}
          isEditing={isEditing}
          onChange={persistHtml}
          onFocusChange={handleFocusChange}
          onExitEditing={onExitEditing}
          toolbarAnchorRef={toolbarAnchorRef}
          headingsOnly={headingsOnly}
          maxPlainTextLength={maxPlainTextLength}
          placeholder={placeholder}
          previewClassName={!isEditing ? previewCursorClass : undefined}
        />
      )}
    </div>
  );
}
